import { Constant } from 'src/constant';
import { environment } from 'src/environments/environment';
import { Language } from '@app/shared/types/Language';

const regexpSmashDomain = /fromsmash.com|fromsmash.ninja/;
/* Retrieve domain without subdomain  */
export function getDomainWithoutSubDomain(url: string): string {
    const urlParts = url.split('.');
    return urlParts
        .slice(0)
        .slice(-(urlParts.length === 4 ? 3 : 2))
        .join('.');
}

/* Used to retrieve main domain + port (if exists). */
/* not used anymore */
export function getProtocolMainDomainAndPort() {
    const protocol = window.location.protocol;
    const port = window.location.port;
    return protocol + '//' + environment.appDomain + (!!port ? ':' + port : '');
}

/* Used to retrieve domain + port (if exists). */
export function getProtocolHostnameAndPort() {
    const url = window.location.href;
    const arr = url.split('/');
    return arr[0] + '//' + arr[2];
}

export function hostnameIsFromMainDomain(url: string) {
    const regExp = new RegExp(`^(www\.)?${environment.appDomain}`);
    return regExp.test(url);
}

export function formatMainDomain() {
    if (typeof window !== 'undefined') {
        const { protocol, hostname, port } = window.location;
        const customCNAME = window.location.hostname.match(regexpSmashDomain) ? false : true;
        if (customCNAME) {
            return `${protocol}//${hostname}${port ? ':' + port : ''}`;
        } else {
            const [, domain] = hostname.match(Constant.domainRegexp);
            return `${protocol}//${domain}${port ? ':' + port : ''}`;
        }
    }
}

export function isHostnameCNAME() {
    return regexpSmashDomain.test(window.location.hostname) ? false : true;
}

export function extractDomainSuffixOrCNAME() {
    if (typeof window !== 'undefined') {
        const { hostname } = window.location;
        if (isHostnameCNAME()) {
            return hostname;
        }
        const [, domain] = hostname.match(Constant.domainRegexp);
        return `.${domain}`;
    }
}

export function getMainDomainSuffix() {
    if (typeof window !== 'undefined') {
        const { hostname } = window.location;
        // Guarded like getMainDomainSuffixServerSide below, which has always checked its match.
        // Constant.domainRegexp requires a dot, so a dotless host ('localhost', a container name)
        // makes match() return null and destructuring it threw -- inside callers such as
        // teams.effects.ts#updateTeam$, that throw happens in a concatMap BEFORE the SDK call, so
        // the effect stream dies without emitting and the form stays behind a spinner forever.
        const regexResult = hostname.match(Constant.domainRegexp);
        if (regexResult && regexResult.length) {
            const [, domain] = regexResult;
            return `.${domain}`;
        }
    }
}

export function getMainDomainSuffixServerSide(host?: string) {
    const regexResult = host.match(Constant.domainRegexp);
    if (regexResult && regexResult.length) {
        const [, domain] = regexResult;
        return `.${domain}`;
    }
}

export function getPathname() {
    if (typeof window !== 'undefined') {
        return window.location.pathname;
    }
}

export function getPathWithoutLastFragment(): string {
    const splittedPath = getPathname().split('/');
    const pathWithoutLastFragment = splittedPath.slice(0, splittedPath.length - 1).join('/');
    return pathWithoutLastFragment;
}

export function getCurrentPath(url: string) {
    let currPath;
    const pageWithSeoREgexExp2 = /\/(fr|de|pt|it|es)?\/?([|signin|signup\/plan?\/[a-zA-Z0-9\s%]+)/;
    const matchPath = url.match(pageWithSeoREgexExp2);
    const regexLang = /^en|fr|de|pt|it|es$/;
    if (matchPath[1]) {
        const [isLang] = matchPath[1].match(regexLang);
        if (isLang && matchPath[2]) {
            currPath = matchPath[2];
        } else {
            currPath = '';
        }
    } else if (matchPath[2]) {
        currPath = matchPath[2];
    } else {
        currPath = '';
    }
    return currPath;
}

export function getCurrentLangFromPath(url: string): Language {
    if (url.split('/')[1] && url.split('/')[1].length !== 2) {
        return 'en';
    }
    const pageWithSeoREgexExp = /\/(fr|de|de|pt|it|es)?\/?(|signin|signup)?/;
    const matchPath = url.match(pageWithSeoREgexExp);
    return matchPath[1] ? matchPath[1] as Language : 'en';
}


export function redirectToAccount(teamId: string) {
    // if hostname is customized redirect to same hostname
    const customCNAME = window.location.hostname.match(regexpSmashDomain) ? false : true;
    if (customCNAME) {
        window.location.href = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/account/team/${teamId}/transfers/sent`;
    } else {
        const [, ext] = window.location.hostname.match(Constant.regexpDomainExtension);
        window.location.href = `${window.location.protocol}//${Constant.redirection.main}${ext}${window.location.port ? ':' + window.location.port : ''}/account/team/${teamId}/transfers/sent`;
    }
}

export function redirectToMainDomain() {
    const [, ext] = document.location.hostname.match(Constant.regexpDomainExtension);
    window.location.href = document.location.protocol + '//' + Constant.redirection.main + ext + (window.location.port ? ':' + window.location.port : '');
}

/**
 * Whether the current document was reached BY a navigation from `host`.
 *
 * Named for what document.referrer actually means: it is set by ANY navigation — a plain link click
 * as much as a `location.href` redirect — and is immutable for the document's lifetime. So this
 * cannot distinguish "was redirected from" from "navigated from", which is exactly why its only
 * caller is an AUTOMATIC redirect decision (DomainRedirectionGuard's team bounce). Do NOT gate an
 * explicit user action on it: a user who merely arrived from that host would have their click
 * silently downgraded for the rest of the document's life.
 *
 * Fails OPEN — an absent or unparseable referrer returns false, so the caller proceeds. For the
 * bounce that uses it, a missed suppression is recoverable by the user, while suppressing it wrongly
 * would strand them. Note this makes the caller's correctness depend on the response
 * `Referrer-Policy` (production sends `strict-origin-when-cross-origin`, which still yields the
 * origin cross-origin — enough, since only the hostname is compared).
 *
 * @param host The hostname to test the referrer against; a falsy value returns false.
 * @returns `true` only when the referrer parses and its hostname equals `host`.
 */
export function wasNavigatedFrom(host: string | undefined): boolean {
    if (!host || typeof document === 'undefined' || !document.referrer) {
        return false;
    }
    try {
        return new URL(document.referrer).hostname === host;
    } catch {
        return false;
    }
}

/**
 * Whether following a domain record's `redirect` would loop back immediately.
 *
 * The redirect itself is an unconditional `window.location.href` assignment, and a JS navigation loop hits
 * NO browser cap — HTTP redirect chains are capped around 20, this is not one. So a record pointing at its
 * own host, or two records pointing at each other, reloads forever for every visitor including anonymous
 * ones, and leaves the domain unreachable so nothing can be repaired from the client.
 *
 * Two synchronous signals, deliberately no network call: this runs on a hot path (every visitor to every
 * redirecting domain), unlike the team-bounce pre-flight which is rare enough to afford a lookup.
 *  - the target resolves to the host we are already on — catches a self-pointing record;
 *  - the target is the host we were just navigated from — catches a mutual pair on its second hop.
 *
 * Longer cycles (three or more records) are NOT caught; each extra link makes the shape less likely, and
 * the durable fix is server-side validation that a `redirect` cannot form one.
 *
 * Comparison is by HOSTNAME, not by string: a stored value carrying a port or a path still points at the
 * same place. It is parsed defensively because it is operator-entered data.
 *
 * @param redirect The record's `redirect` value — a bare host in production, possibly with port or path.
 * @returns `true` when the redirect must NOT be followed.
 */
export function redirectWouldLoop(redirect: string | undefined): boolean {
    if (!redirect || typeof window === 'undefined') {
        return false;
    }
    const targetHost = parseHostname(redirect);
    if (!targetHost) {
        // Unparseable: refuse rather than hand a malformed value to location.href, which would navigate
        // somewhere unintended instead of failing visibly.
        return true;
    }
    if (targetHost === window.location.hostname) {
        return true;
    }
    return typeof document !== 'undefined' && !!document.referrer && parseHostname(document.referrer) === targetHost;
}

/**
 * Extracts a hostname from either a full URL or a bare host (optionally carrying a port or path).
 *
 * @param value The value to read a hostname from.
 * @returns The hostname, or `undefined` when it cannot be parsed.
 */
function parseHostname(value: string): string | undefined {
    try {
        // A bare host has no scheme, so prefixing one lets both forms go through the same parser instead
        // of a hand-rolled split.
        return new URL(value.includes('://') ? value : `https://${value}`).hostname || undefined;
    } catch {
        return undefined;
    }
}

// To know if you are on a CNAME or not
export function isAppDomain() {
    return window.location.hostname.match(environment.appDomain) ? true : false;
}
